/*!
 * DisplayDetails Content Fixes
 * Theme-side content overrides — loaded on every page.
 * Fixes copy errors that live in product/page body_html which we cannot edit
 * via the Theme Access token. Replaces text server-rendered into the DOM.
 *
 * Strategy: walk text nodes only (never touch scripts/styles/inputs),
 * apply global replacements, then apply page-specific fixes keyed by pathname.
 *
 * Build: pdp-collection-rev7 (2026-04-20) — fix free-shipping copy in trust block, add install + CMS checkbox add-ons with dynamic quote-CTA
 */
(function () {
  'use strict';

  // --- Global text replacements ---------------------------------------------
  var GLOBAL = [
    // Phones — canonical: (201) 834-6412
    [/\(347\)\s*410[-\s]*6890/g, '(201) 834-6412'],
    [/347[-.\s]*410[-.\s]*6890/g, '(201) 834-6412'],
    [/\(555\)\s*123[-\s]*4567/g, '(201) 834-6412'],
    [/902\.214\.1290/g, '(201) 834-6412'],

    // Emails — canonical: hello@displaydetails.com
    [/info@crowntv-us\.com/g, 'hello@displaydetails.com'],
    [/support@displaydetails\.com/g, 'hello@displaydetails.com'],

    // Broken template/copy-paste artifacts
    [/FREE Shipping Subscription/g, 'FREE Shipping'],
    [/1 free shipping to power your digital signage content/g,
      'free shipping'],
    [/1 month free shipping/gi, 'free shipping'],
    [/1 free shipping per screen/gi, 'free shipping'],

    // Brand de-entanglement: strip any "DisplayDetails by CrownTV" lockup that
    // leaks in from admin-side saved settings. DisplayDetails is a standalone brand.
    [/DisplayDetails by CrownTV Advantage/g, 'DisplayDetails Advantage'],
    [/DisplayDetails by CrownTV/g, 'DisplayDetails'],

    // Form placeholder scrubs
    [/john@company\.com/gi, 'you@yourcompany.com'],

    // Stale year references
    [/\b2024 buyer'?s guide\b/gi, '2026 buyer\u2019s guide'],
    [/\bin 2024\b/g, 'in 2026'],
    [/\bfor 2024\b/g, 'for 2026'],

    // Outdated pricing language
    [/\$19\.99\/mo\b/g, '$29.99/mo'],
    [/\$24\.99\/mo\b/g, '$29.99/mo'],

    // ViewSonic scrub — belt-and-suspenders safety net for any text that
    // leaks through from admin-side saved settings we can't edit.
    [/Samsung,\s*LG\s*(?:&amp;|&|and)\s*ViewSonic/gi, 'Samsung & LG'],
    [/Samsung,\s*LG,?\s*(?:and)?\s*ViewSonic/gi, 'Samsung and LG'],
    [/Samsung\s*\/\s*LG\s*\/\s*ViewSonic/gi, 'Samsung / LG'],
    [/\bViewSonic Digital Signage\b/g, ''],
    [/\bViewSonic Commercial Displays\b/g, '']
  ];

  // --- Per-product / per-page replacements ----------------------------------
  // Matched against location.pathname (lowercased)
  var PAGE_RULES = {
    '/products/samsung-oh55b': [
      // OH55A → OH55B everywhere except canonical contexts (it's all wrong model)
      [/Samsung\s+OH55A/g, 'Samsung OH55B'],
      [/\bOH55A\b/g, 'OH55B'],
      [/the OH55B\s+55/g, 'the OH55B 55'],
      // Promo line fix
      [/\(then \$29\.99\/mo\)/g, '(then $29.99/mo for CMS)']
    ],
    '/products/samsung-qe43t-43-4k-uhd-commercial-led-display': [
      [/QHC series/gi, 'QH*C series'],
      [/\(then \$29\.99\/mo\)/g, '(then $29.99/mo for CMS)']
    ],
    '/products/samsung-qm55c': [
      [/\(then \$29\.99\/mo\)/g, '(then $29.99/mo for CMS)']
    ],
    '/products/samsung-the-wall-all-in-one-146-microled-display-p1-6': [
      // Replace misleading refresh-rate claim with accurate spec
      [/3,?840\s*Hz\s*(?:refresh rate|frame rate)[^.]*\./gi,
        '120 Hz panel refresh rate with 3,840 Hz PWM dimming for ultra-smooth motion.']
    ],
    '/products/lg-55um5j-55-signage-display': [
      // Dimensions/weight look copy-pasted from another model; add a disclaimer footnote via CSS marker
      // (we can't know the right values without manufacturer spec sheet, so flag it)
    ]
  };

  // --- Page-level content blocks to inject ----------------------------------
  // These render before existing page content as a "canonical card."
  var PAGE_INJECTIONS = {
    '/pages/shipping-return-policy': buildShippingPolicyCard,
    '/pages/frequently-asked-questions': buildFaqCard,
    '/pages/samsung-vs-lg-commercial-displays': buildSamsungVsLgCard,
    '/pages/our-installations': buildInstallationsCard,
    '/pages/about': buildAboutCard,
    '/pages/contact-us': cleanContactPage
  };

  // --- Implementation -------------------------------------------------------

  function walkTextNodes(root, rules) {
    if (!root || !rules || !rules.length) return;
    var SKIP = { SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEXTAREA: 1, INPUT: 1, SELECT: 1, CODE: 1, PRE: 1 };
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (n) {
        var p = n.parentNode;
        while (p && p.nodeType === 1) {
          if (SKIP[p.tagName]) return NodeFilter.FILTER_REJECT;
          p = p.parentNode;
        }
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    var nodes = [];
    var cur;
    while ((cur = walker.nextNode())) nodes.push(cur);
    for (var i = 0; i < nodes.length; i++) {
      var t = nodes[i].nodeValue;
      var changed = false;
      for (var j = 0; j < rules.length; j++) {
        var re = rules[j][0];
        var rep = rules[j][1];
        if (re.test(t)) {
          t = t.replace(re, rep);
          changed = true;
          re.lastIndex = 0; // in case of /g flag re-use
        }
      }
      if (changed) nodes[i].nodeValue = t;
    }
  }

  function walkAttributes(root, rules) {
    // Fix hrefs and placeholders that contain old phones/emails
    var els = root.querySelectorAll('a[href^="tel:"], a[href^="mailto:"], input[placeholder], textarea[placeholder]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.hasAttribute('href')) {
        var h = el.getAttribute('href');
        h = h.replace(/3474106890/g, '2018346412')
             .replace(/5551234567/g, '2018346412')
             .replace(/9022141290/g, '2018346412')
             .replace(/info@crowntv-us\.com/g, 'hello@displaydetails.com')
             .replace(/support@displaydetails\.com/g, 'hello@displaydetails.com');
        el.setAttribute('href', h);
      }
      if (el.hasAttribute('placeholder')) {
        var p = el.getAttribute('placeholder');
        p = p.replace(/john@company\.com/gi, 'you@yourcompany.com')
             .replace(/\(555\)\s*123[-\s]*4567/g, '(201) 834-6412');
        el.setAttribute('placeholder', p);
      }
    }
  }

  function buildShippingPolicyCard() {
    return '' +
      '<div class="dd-policy-card" style="background:#F0FBFE;border-left:4px solid #00BBE1;padding:24px 28px;border-radius:8px;margin:0 0 32px 0;font-family:-apple-system,BlinkMacSystemFont,\'Segoe UI\',Roboto,Arial,sans-serif;">' +
        '<h2 style="margin:0 0 12px 0;font-size:22px;color:#1A2128;">DisplayDetails Shipping &amp; Returns &mdash; 2026 Policy</h2>' +
        '<p style="margin:0 0 12px 0;font-size:16px;line-height:1.6;color:#1A2128;"><strong>Shipping:</strong> All commercial displays ship free via insured freight to any address in the contiguous United States. Orders ship within 5&ndash;7 business days; email <a href="mailto:hello@displaydetails.com">hello@displaydetails.com</a> for faster processing. We do not ship to Alaska, Hawaii, US territories or international addresses.</p>' +
        '<p style="margin:0 0 12px 0;font-size:16px;line-height:1.6;color:#1A2128;"><strong>Warranty &amp; returns:</strong> Units are covered by the manufacturer warranty that ships with each display. Return windows and refund terms are handled case-by-case &mdash; email <a href="mailto:hello@displaydetails.com" style="color:#00BBE1;">hello@displaydetails.com</a> or call <a href="tel:+12018346412" style="color:#00BBE1;">(201) 834-6412</a> before shipping anything back.</p>' +
        '<p style="margin:0 0 12px 0;font-size:16px;line-height:1.6;color:#1A2128;"><strong>Damage claims:</strong> Inspect your shipment on delivery. Photograph any carton damage before signing for delivery and contact us within 48 hours at <a href="mailto:hello@displaydetails.com" style="color:#00BBE1;">hello@displaydetails.com</a> or <a href="tel:+12018346412" style="color:#00BBE1;">(201) 834-6412</a>. We handle freight claims on your behalf.</p>' +
        '<p style="margin:0;font-size:14px;color:#555;font-style:italic;">Questions on a specific project or bulk deployment? Call <a href="tel:+12018346412" style="color:#00BBE1;">(201) 834-6412</a> or email <a href="mailto:hello@displaydetails.com" style="color:#00BBE1;">hello@displaydetails.com</a>.</p>' +
      '</div>';
  }

  function buildFaqCard() {
    return '' +
      '<div class="dd-faq-card" style="background:#FFFBF0;border-left:4px solid #FF9F2E;padding:24px 28px;border-radius:8px;margin:0 0 32px 0;font-family:-apple-system,BlinkMacSystemFont,\'Segoe UI\',Roboto,Arial,sans-serif;">' +
        '<h2 style="margin:0 0 12px 0;font-size:22px;color:#1A2128;">Most-Asked Questions</h2>' +
        '<dl style="margin:0;">' +
          '<dt style="font-weight:700;font-size:16px;margin:12px 0 4px 0;color:#1A2128;">How is shipping handled?</dt>' +
          '<dd style="margin:0 0 12px 0;font-size:15px;line-height:1.6;color:#1A2128;">Every display ships free via insured freight to the contiguous United States. Orders ship within 5&ndash;7 business days; email <a href="mailto:hello@displaydetails.com">hello@displaydetails.com</a> for faster processing. We do not ship to Alaska, Hawaii, US territories or international addresses.</dd>' +
          '<dt style="font-weight:700;font-size:16px;margin:12px 0 4px 0;color:#1A2128;">What warranty do I get?</dt>' +
          '<dd style="margin:0 0 12px 0;font-size:15px;line-height:1.6;color:#1A2128;">The standard manufacturer warranty that ships with each Samsung or LG commercial display. Terms vary by model. Warranty service is handled directly with the manufacturer &mdash; email <a href="mailto:hello@displaydetails.com" style="color:#00BBE1;">hello@displaydetails.com</a> for the specific terms on a SKU.</dd>' +
          '<dt style="font-weight:700;font-size:16px;margin:12px 0 4px 0;color:#1A2128;">Can we buy on a purchase order?</dt>' +
          '<dd style="margin:0 0 12px 0;font-size:15px;line-height:1.6;color:#1A2128;">Yes. Send the model and quantity through the <a href="/pages/purchase-orders#quote" style="color:#00BBE1;">PO quote form</a>; a formal quote and invoice come back within 1 business day, payable by PO, ACH/wire, or card. Tax-exempt certificates are honored and a W-9 is available on request.</dd>' +
          '<dt style="font-weight:700;font-size:16px;margin:12px 0 4px 0;color:#1A2128;">Do you offer installation?</dt>' +
          '<dd style="margin:0 0 12px 0;font-size:15px;line-height:1.6;color:#1A2128;">Yes &mdash; professional installation is available and is scheduled at quote stage. Request a site-specific quote on the contact page or call <a href="tel:+12018346412" style="color:#00BBE1;">(201) 834-6412</a>.</dd>' +
          '<dt style="font-weight:700;font-size:16px;margin:12px 0 4px 0;color:#1A2128;">Can I get volume pricing?</dt>' +
          '<dd style="margin:0;font-size:15px;line-height:1.6;color:#1A2128;">Yes, for orders of 5 screens or more. Email <a href="mailto:hello@displaydetails.com" style="color:#00BBE1;">hello@displaydetails.com</a> with your quantity and model list for a quote within 1 business day.</dd>' +
        '</dl>' +
      '</div>';
  }

  function buildSamsungVsLgCard() {
    return '' +
      '<div class="dd-compare-card" style="background:#fff;border:1px solid #E5E7EB;padding:28px;border-radius:10px;margin:0 0 32px 0;font-family:-apple-system,BlinkMacSystemFont,\'Segoe UI\',Roboto,Arial,sans-serif;box-shadow:0 2px 8px rgba(0,0,0,0.04);">' +
        '<h2 style="margin:0 0 16px 0;font-size:24px;color:#1A2128;">Samsung vs LG Commercial Displays &mdash; Which to Choose in 2026</h2>' +
        '<p style="font-size:16px;line-height:1.7;color:#333;margin:0 0 16px 0;">Both Samsung and LG dominate the commercial-display market, but they optimize for different deployment profiles. Here\'s a plain-English rundown of when each brand wins.</p>' +
        '<h3 style="font-size:18px;margin:20px 0 8px 0;color:#1A2128;">Choose Samsung when you need</h3>' +
        '<ul style="font-size:15px;line-height:1.8;color:#333;margin:0 0 16px 24px;padding:0;">' +
          '<li><strong>Highest brightness for window signage or outdoor:</strong> the OM and OH series reach up to 3,500&ndash;4,000 nits, unmatched in class.</li>' +
          '<li><strong>Largest all-in-one video walls:</strong> The Wall MicroLED platform scales from 146&quot; to 1,000&quot;+ without bezels.</li>' +
          '<li><strong>Tizen-based on-device CMS:</strong> MagicInfo runs natively on the display, cutting media-player costs.</li>' +
          '<li><strong>Broadest model catalog:</strong> QM, QH, QB, PH, DB, OH, OM families cover every size, brightness, and budget tier.</li>' +
        '</ul>' +
        '<h3 style="font-size:18px;margin:20px 0 8px 0;color:#1A2128;">Choose LG when you need</h3>' +
        '<ul style="font-size:15px;line-height:1.8;color:#333;margin:0 0 16px 24px;padding:0;">' +
          '<li><strong>webOS-first fleet management:</strong> LG\'s SuperSign CMS and webOS ecosystem integrates well with existing enterprise device-management tools.</li>' +
          '<li><strong>Thin-bezel video walls:</strong> the VH7 and VM5J series deliver 0.44mm bezel-to-bezel for tight multi-screen arrays.</li>' +
          '<li><strong>24/7 commercial workloads at lower entry price:</strong> the UM5J and UH5J series are popular for corporate signage at more accessible price points.</li>' +
          '<li><strong>Transparent OLED or stretched bar displays:</strong> LG makes specialty form factors Samsung doesn\'t.</li>' +
        '</ul>' +
        '<h3 style="font-size:18px;margin:20px 0 8px 0;color:#1A2128;">Our bottom line</h3>' +
        '<p style="font-size:16px;line-height:1.7;color:#333;margin:0 0 16px 0;">For <em>most</em> retail, QSR, and office signage deployments, Samsung\'s QM-C and QB-C series win on brightness, color, and built-in CMS. For <em>24/7 corporate lobby and wayfinding</em> where budget is the tiebreaker, LG UM5J is a strong value. For <em>outdoor or window-facing</em> placements, Samsung OH or OM is almost always the right call.</p>' +
        '<p style="font-size:16px;line-height:1.7;color:#333;margin:0;">Not sure? <a href="tel:+12018346412" style="color:#00BBE1;font-weight:600;">Call (201) 834-6412</a> or email <a href="mailto:hello@displaydetails.com" style="color:#00BBE1;font-weight:600;">hello@displaydetails.com</a> with your use case and we\'ll spec a solution in one call.</p>' +
      '</div>';
  }

  function buildInstallationsCard() {
    return '' +
      '<div class="dd-install-card" style="background:linear-gradient(135deg,#1A2128 0%,#00BBE1 100%);color:#fff;padding:36px;border-radius:12px;margin:0 0 32px 0;font-family:-apple-system,BlinkMacSystemFont,\'Segoe UI\',Roboto,Arial,sans-serif;">' +
        '<h2 style="margin:0 0 16px 0;font-size:26px;color:#fff;">Commercial Display Installation</h2>' +
        '<p style="font-size:16px;line-height:1.7;margin:0 0 20px 0;">DisplayDetails coordinates commercial display installation across all 50 US states &mdash; from single-screen menu boards in QSR locations to multi-screen corporate rollouts and MicroLED video walls in broadcast studios.</p>' +
        '<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:16px;margin:24px 0;">' +
          '<div><div style="font-size:28px;font-weight:700;">50</div><div style="font-size:13px;opacity:0.85;text-transform:uppercase;letter-spacing:1px;">US States Covered</div></div>' +
          '<div><div style="font-size:28px;font-weight:700;">3&ndash;14</div><div style="font-size:13px;opacity:0.85;text-transform:uppercase;letter-spacing:1px;">Day Install Lead Time</div></div>' +
        '</div>' +
        '<h3 style="margin:24px 0 8px 0;color:#fff;font-size:18px;">What a typical install includes</h3>' +
        '<ul style="font-size:15px;line-height:1.8;margin:0 0 20px 20px;padding:0;">' +
          '<li>Site survey and mount-location engineering (wall, ceiling, freestanding, or recessed)</li>' +
          '<li>Professional low-voltage technician dispatched to your site</li>' +
          '<li>VESA-compliant mounting, cable management, power and signal routing</li>' +
          '<li>CMS onboarding and first-content deployment on-site</li>' +
          '<li>Manufacturer-warranty registration and first-year support escalation path</li>' +
        '</ul>' +
        '<a href="/pages/contact-us" style="display:inline-block;background:#FF9F2E;color:#1A2128;padding:14px 28px;border-radius:6px;text-decoration:none;font-weight:700;font-size:15px;margin-top:12px;">Request an Installation Quote &rarr;</a>' +
        '<span style="margin-left:12px;font-size:14px;opacity:0.9;">or call <a href="tel:+12018346412" style="color:#fff;text-decoration:underline;">(201) 834-6412</a></span>' +
      '</div>';
  }

  function buildAboutCard() {
    return '' +
      '<div class="dd-about-card" style="background:#fff;border:1px solid #E5E7EB;padding:32px;border-radius:12px;margin:0 0 32px 0;font-family:-apple-system,BlinkMacSystemFont,\'Segoe UI\',Roboto,Arial,sans-serif;box-shadow:0 2px 12px rgba(0,0,0,0.04);">' +
        '<h2 style="margin:0 0 12px 0;font-size:26px;color:#1A2128;">About DisplayDetails</h2>' +
        '<p style="font-size:17px;line-height:1.7;color:#333;margin:0 0 16px 0;"><strong>DisplayDetails is an Authorized Samsung Reseller</strong> and direct-to-business seller of genuine Samsung and LG commercial displays &mdash; serving US businesses with free shipping in the contiguous US, professional installation, and content-management support.</p>' +
        '<p style="font-size:16px;line-height:1.7;color:#333;margin:0 0 16px 0;">We sell genuine commercial displays from <strong>Samsung and LG</strong>, with professional installation available and purchase orders and tax-exempt sales accepted. No middleman markups, no gray-market imports, no warranty gotchas &mdash; every display is a genuine unit with full manufacturer coverage.</p>' +
        '<h3 style="margin:24px 0 8px 0;font-size:18px;color:#1A2128;">Why businesses choose us</h3>' +
        '<ul style="font-size:15px;line-height:1.8;color:#333;margin:0 0 20px 24px;padding:0;">' +
          '<li><strong>Genuine stock:</strong> full manufacturer warranty, direct-from-vendor units</li>' +
          '<li><strong>Free US shipping</strong> on every display, insured and tracked</li>' +
          '<li><strong>One-call specifier:</strong> we spec your install, ship the screens, and dispatch the installers &mdash; you deal with one company</li>' +
          '<li><strong>Volume pricing</strong> on orders of 5 screens or more</li>' +
          '<li><strong>RFP-ready:</strong> we submit specs, compliance docs, and bid-ready quotes for government and enterprise buyers</li>' +
        '</ul>' +
        '<p style="font-size:15px;line-height:1.7;color:#555;margin:0;">Have a deployment coming up? Talk to a real person: <a href="tel:+12018346412" style="color:#00BBE1;font-weight:600;">(201) 834-6412</a> or <a href="mailto:hello@displaydetails.com" style="color:#00BBE1;font-weight:600;">hello@displaydetails.com</a>.</p>' +
      '</div>';
  }

  function cleanContactPage() {
    // Contact page has duplicate forms + placeholder data; hide the stray extra forms
    // Returns empty string (just for consistent API) but also schedules DOM cleanup.
    setTimeout(function () {
      try {
        // Hide extra duplicate <form> elements beyond the first two (primary + Shopify default)
        var forms = document.querySelectorAll('main form, #MainContent form, .rte form');
        if (forms.length > 2) {
          for (var i = 2; i < forms.length; i++) {
            forms[i].style.display = 'none';
          }
        }
        // Scrub placeholder junk visible as static text
        var body = document.querySelector('main') || document.body;
        walkTextNodes(body, [
          [/john@company\.com/gi, 'you@yourcompany.com'],
          [/\(555\)\s*123[-\s]*4567/g, '(201) 834-6412'],
          [/902\.214\.1290/g, '(201) 834-6412']
        ]);
      } catch (e) { /* no-op */ }
    }, 0);
    return '';
  }

  // --- Product-page universal trust block ----------------------------------
  // Appended at BOTTOM of every product description so thin products get value.
  function buildProductTrustBlock() {
    return '' +
      '<div class="dd-product-trust" style="margin:32px 0 0 0;padding:28px;background:linear-gradient(180deg,#F0FBFE 0%,#fff 100%);border:1px solid #D6EEF5;border-radius:10px;font-family:-apple-system,BlinkMacSystemFont,\'Segoe UI\',Roboto,Arial,sans-serif;">' +
        '<h3 style="margin:0 0 16px 0;font-size:20px;color:#1A2128;">What you get with every DisplayDetails order</h3>' +
        '<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:16px;">' +
          '<div><div style="font-size:15px;font-weight:700;color:#00BBE1;margin-bottom:4px;">&#10004; Free US Shipping</div><div style="font-size:13px;color:#333;line-height:1.5;">Free insured freight to every address in the contiguous United States. Orders ship within 5&ndash;7 business days; email us for faster processing.</div></div>' +
          '<div><div style="font-size:15px;font-weight:700;color:#00BBE1;margin-bottom:4px;">&#10004; Authorized Samsung Reseller</div><div style="font-size:13px;color:#333;line-height:1.5;">Genuine Samsung (and LG) commercial stock sold as an Authorized Samsung Reseller, covered by the full manufacturer warranty that ships with the unit.</div></div>' +
          '<div><div style="font-size:15px;font-weight:700;color:#00BBE1;margin-bottom:4px;">&#10004; Purchase Orders</div><div style="font-size:13px;color:#333;line-height:1.5;">Purchase orders, ACH/wire, and tax-exempt orders accepted. Quotes back within 1 business day.</div></div>' +
          '<div><div style="font-size:15px;font-weight:700;color:#00BBE1;margin-bottom:4px;">&#10004; Installation Available</div><div style="font-size:13px;color:#333;line-height:1.5;">Optional professional installation, scheduled at quote stage. Request a quote when you check out.</div></div>' +
          '<div><div style="font-size:15px;font-weight:700;color:#00BBE1;margin-bottom:4px;">&#10004; Volume Pricing</div><div style="font-size:13px;color:#333;line-height:1.5;">5+ screens? Email <a href="mailto:hello@displaydetails.com" style="color:#00BBE1;">hello@displaydetails.com</a> for pricing within 1 business day.</div></div>' +
          '<div><div style="font-size:15px;font-weight:700;color:#00BBE1;margin-bottom:4px;">&#10004; Real Humans</div><div style="font-size:13px;color:#333;line-height:1.5;">Call <a href="tel:+12018346412" style="color:#00BBE1;font-weight:600;">(201) 834-6412</a> &mdash; our team ships and installs displays every day.</div></div>' +
        '</div>' +
        '<p style="margin:20px 0 0 0;font-size:14px;color:#555;text-align:center;">Need this spec\'d into a larger deployment? <a href="/pages/contact-us" style="color:#00BBE1;font-weight:600;">Request a volume quote &rarr;</a></p>' +
      '</div>';
  }

  // Per-series summary shown inline on product pages matching the prefix.
  var SERIES_SUMMARIES = [
    {
      match: /^\/products\/samsung-qm(\d+)c/,
      html: 'Samsung\u2019s <strong>QM-C series</strong> is the flagship 500-nit 4K UHD commercial LCD line for 24/7 indoor deployments &mdash; retail, corporate, QSR. Built-in Tizen MagicInfo CMS, HDMI/DisplayPort/USB/LAN/RS-232C, VESA mount, 3-year commercial warranty.'
    },
    {
      match: /^\/products\/samsung-qb(\d+)c/,
      html: 'Samsung\u2019s <strong>QB-C series</strong> is the value-tier 350-nit 4K UHD commercial display &mdash; ideal for lobbies, conference rooms, and signage with 16/7 duty cycles. Tizen-based with MagicInfo, VESA mount, 3-year commercial warranty.'
    },
    {
      match: /^\/products\/samsung-qh(\d+)c/,
      html: 'Samsung\u2019s <strong>QH-C series</strong> delivers 700-nit high-brightness 4K UHD for semi-outdoor, window-facing, and brightly-lit retail environments. 24/7 rated, Tizen-based with MagicInfo, 3-year warranty.'
    },
    {
      match: /^\/products\/samsung-oh(\d+)[ab]/,
      html: 'Samsung\u2019s <strong>OH series</strong> is a fully weatherproof (IP56) outdoor commercial display with 3,500-nit brightness. Designed for drive-thrus, gas-station canopies, and storefronts. Operates from &minus;30&deg;C to +50&deg;C. 3-year warranty.'
    },
    {
      match: /^\/products\/samsung-om(\d+)[ab]/,
      html: 'Samsung\u2019s <strong>OM series</strong> is a 3,000 to 4,000-nit high-brightness indoor display built for window-facing retail and direct-sunlight placements. Auto-brightness control, Tizen MagicInfo, VESA mount, 3-year warranty.'
    },
    {
      match: /^\/products\/samsung-qe\d+t/,
      html: 'Samsung\u2019s <strong>QE-T series</strong> is an entry-tier 4K UHD commercial display with 300-nit brightness, ideal for indoor signage on a budget. Tizen-based with embedded CMS support, VESA mount, 3-year warranty.'
    },
    {
      match: /^\/products\/samsung-be\d+/,
      html: 'Samsung\u2019s <strong>BE-DH / BE-TH / BE-FXH series</strong> is a business-grade 4K smart TV optimized for conference rooms, hospitality, and light-signage use. Tizen with the Business Launcher UI, HDMI/USB/LAN, 2-year commercial warranty.'
    },
    {
      match: /^\/products\/lg-\d+um5j/,
      html: 'LG\u2019s <strong>UM5J series</strong> is a 400-nit 4K UHD 24/7 commercial display for corporate signage and wayfinding. webOS 6.0 with SuperSign CMS, HDMI/DisplayPort/USB/RS-232C, VESA mount, 3-year warranty.'
    },
    {
      match: /^\/products\/lg-\d+uh5j/,
      html: 'LG\u2019s <strong>UH5J series</strong> is a 700-nit high-brightness 4K UHD commercial display for window-facing and brightly-lit retail. webOS 6.0 with SuperSign CMS, 24/7 rated, VESA mount, 3-year warranty.'
    }
  ];

  function injectProductPageContent(path) {
    // Find the DESCRIPTION block in the right column — specifically the
    // Horizon text-block with id suffix "__text_aEtTtq" (product description).
    // Never fall back to a generic ".rte" because that also matches the
    // title block and the <product-price> element (both carry .rte).
    function findDescription() {
      // Preferred: exact description block class Horizon emits
      var q = document.querySelector('[class*="__text_aEtTtq"]');
      if (q) return q;
      // Alt: description block data-block-id
      q = document.querySelector('[data-block-id*="text_aEtTtq"]');
      if (q) return q;
      // Alt: explicit product description class if present
      q = document.querySelector('.product__description, .product-description, [data-product-description]');
      if (q) return q;
      // Last resort: pick the LAST .rte inside the product-details column —
      // that is the body copy, not the title and not the price.
      var col = document.querySelector('.product-details, [class*="_product_details"], .product-information') || document.body;
      var rtes = col.querySelectorAll('.rte');
      for (var i = rtes.length - 1; i >= 0; i--) {
        var el = rtes[i];
        // Skip elements that are the title, price, or inside a product card
        if (el.closest('product-price')) continue;
        if (el.closest('[class*="_product_title"]')) continue;
        if (el.closest('.product-card, .card-gallery, .predictive-search-results')) continue;
        return el;
      }
      return null;
    }
    var desc = findDescription();
    if (!desc) return;

    // Series summary -> insert at TOP of description if not already present
    for (var i = 0; i < SERIES_SUMMARIES.length; i++) {
      if (SERIES_SUMMARIES[i].match.test(path)) {
        if (!desc.querySelector('.dd-series-summary')) {
          var sumWrap = document.createElement('div');
          sumWrap.className = 'dd-series-summary';
          sumWrap.style.cssText = 'background:#FFFBF0;border-left:3px solid #FF9F2E;padding:16px 20px;border-radius:6px;margin:0 0 20px 0;font-size:15px;line-height:1.6;color:#1A2128;';
          sumWrap.innerHTML = SERIES_SUMMARIES[i].html;
          desc.insertBefore(sumWrap, desc.firstChild);
        }
        break;
      }
    }

    // Trust block -> append to BOTTOM of description
    if (!desc.querySelector('.dd-product-trust')) {
      var trust = document.createElement('div');
      trust.innerHTML = buildProductTrustBlock();
      desc.appendChild(trust.firstChild);
    }
  }

  // --- Blog page cleanup ---------------------------------------------------
  var BLOG_RULES = [
    // Old prices / claims that persist in blog bodies
    [/toll-free line/gi, 'direct line'],
    [/1-800-[\d-]+/g, '(201) 834-6412'],
    // Stale 2023 references
    [/\b2023 buyer'?s guide\b/gi, '2026 buyer\u2019s guide'],
    [/\bas of 2023\b/gi, 'as of 2026'],
    // Old "shipping costs $100-$200" etc that contradict free-shipping
    [/shipping (?:usually )?costs? \$\d+[-\u2013]\$?\d+/gi, 'shipping is free on every order in the contiguous US']
  ];

  function applyBlogCleanup(path) {
    if (path.indexOf('/blogs/') !== 0) return;
    var article = document.querySelector('article') || document.querySelector('.article__content') || document.querySelector('main');
    if (article) walkTextNodes(article, BLOG_RULES);
  }

  // --- Cart enhancements ---------------------------------------------------
  function enhanceCartPage() {
    if (location.pathname !== '/cart') return;
    // Add a free-shipping reassurance banner at the top of the cart content
    var target =
      document.querySelector('.cart__items') ||
      document.querySelector('cart-items') ||
      document.querySelector('#main-cart-items') ||
      document.querySelector('main .cart') ||
      document.querySelector('main');
    if (!target || target.querySelector('.dd-cart-banner')) return;

    var banner = document.createElement('div');
    banner.className = 'dd-cart-banner';
    banner.style.cssText = 'background:linear-gradient(90deg,#00BBE1 0%,#0089A8 100%);color:#fff;padding:14px 20px;border-radius:8px;margin:0 0 20px 0;display:flex;align-items:center;gap:14px;font-family:-apple-system,BlinkMacSystemFont,\'Segoe UI\',Roboto,Arial,sans-serif;';
    banner.innerHTML =
      '<div style="font-size:24px;line-height:1;">&#128100;</div>' +
      '<div>' +
        '<div style="font-weight:700;font-size:15px;">Need help with your order?</div>' +
        '<div style="font-size:13px;opacity:0.95;">Call <a href="tel:+12018346412" style="color:#fff;text-decoration:underline;font-weight:600;">(201) 834-6412</a> before checkout for volume pricing, installation, or CMS questions.</div>' +
      '</div>';
    target.insertBefore(banner, target.firstChild);
  }

  // --- Product price bulletproof: ensure visible on every PDP -------------
  // Apr 20 2026: Horizon's <product-price> block was rendering an empty or
  // hidden element on some PDPs. This fallback guarantees a visible price.
  function moneyFmt(cents) {
    if (cents === null || cents === undefined) return '';
    var n = Number(cents);
    if (!isFinite(n)) return '';
    var dollars = n / 100;
    return '$' + dollars.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function readProductJson() {
    // Shopify embeds product JSON in several places; try them in order.
    var scripts = document.querySelectorAll(
      'script[type="application/json"][data-product-json], ' +
      'script[type="application/json"][id*="ProductJson"], ' +
      'script[type="application/json"][id*="product-json"]'
    );
    for (var i = 0; i < scripts.length; i++) {
      try {
        var obj = JSON.parse(scripts[i].textContent || '{}');
        if (obj && (obj.price !== undefined || obj.variants)) return obj;
      } catch (e) {}
    }
    // Schema.org JSON-LD fallback
    var ld = document.querySelectorAll('script[type="application/ld+json"]');
    for (var j = 0; j < ld.length; j++) {
      try {
        var d = JSON.parse(ld[j].textContent || '{}');
        if (d && d['@type'] === 'Product' && d.offers) {
          var offers = d.offers;
          var low = offers.lowPrice || offers.price;
          var high = offers.highPrice || offers.price;
          if (low) {
            return {
              __fromLd: true,
              price: Math.round(parseFloat(low) * 100),
              compare_at_price: null,
              price_max: Math.round(parseFloat(high) * 100)
            };
          }
        }
      } catch (e) {}
    }
    // Last-ditch: window.ShopifyAnalytics
    try {
      var sa = window.ShopifyAnalytics;
      if (sa && sa.meta && sa.meta.product) {
        var p = sa.meta.product;
        var v = (p.variants && p.variants[0]) || {};
        return {
          price: v.price || p.price,
          compare_at_price: v.compare_at_price || null
        };
      }
    } catch (e) {}
    return null;
  }

  function priceEmptyOrHidden(el) {
    if (!el) return true;
    var txt = (el.textContent || '').replace(/\s+/g, ' ').trim();
    // strip visually-hidden helpers (Sale price / Regular price labels)
    var spans = el.querySelectorAll('.visually-hidden');
    for (var i = 0; i < spans.length; i++) {
      txt = txt.replace((spans[i].textContent || '').trim(), '');
    }
    txt = txt.trim();
    if (!/\$/.test(txt) && !/\d/.test(txt)) return true;
    var cs = window.getComputedStyle(el);
    if (!cs) return false;
    if (cs.display === 'none') return true;
    if (cs.visibility === 'hidden') return true;
    if (parseFloat(cs.opacity) === 0) return true;
    var rect = el.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return true;
    return false;
  }

  function buildFallbackPriceHtml(pj) {
    if (!pj) return '';
    var priceCents = pj.price;
    // Shopify product JSON stores price in cents already; LD-JSON fallback does too (we already multiplied)
    var cmp = pj.compare_at_price;
    var html = '';
    if (cmp && cmp > priceCents) {
      html += '<span class="dd-price-compare">' + moneyFmt(cmp) + '</span>';
    }
    html = '<span class="dd-price-main">' + moneyFmt(priceCents) + '</span>' + html;
    return html;
  }

  function ensureProductPrice() {
    var container = document.querySelector('.product-information') ||
                    document.querySelector('[class*="product-information"]') ||
                    document.querySelector('main');
    if (!container) return;

    var existing = container.querySelector('product-price, .product__price, [data-block-id*="price"]');

    // Case A: existing element has content and is visible — ensure it's styled clearly and done
    if (existing && !priceEmptyOrHidden(existing)) {
      existing.style.display = 'block';
      existing.style.visibility = 'visible';
      existing.style.opacity = '1';
      return;
    }

    // Case B: existing element but empty/hidden — force styles and also inject fallback after it
    var pj = readProductJson();
    if (!pj) return;

    var fallback = document.createElement('div');
    fallback.className = 'dd-price-fallback';
    fallback.innerHTML = buildFallbackPriceHtml(pj);

    // Insert right after title if present, otherwise at top of container
    var titleEl = container.querySelector('h1') ||
                  container.querySelector('.product__title') ||
                  container.querySelector('[class*="product_title"]');
    if (titleEl && titleEl.parentNode) {
      // don't double-inject
      if (!titleEl.parentNode.querySelector('.dd-price-fallback')) {
        titleEl.parentNode.insertBefore(fallback, titleEl.nextSibling);
      }
    } else if (existing && existing.parentNode) {
      if (!existing.parentNode.querySelector('.dd-price-fallback')) {
        existing.parentNode.insertBefore(fallback, existing.nextSibling);
      }
    } else {
      if (!container.querySelector('.dd-price-fallback')) {
        container.insertBefore(fallback, container.firstChild);
      }
    }

    // Also force-show the existing price if present
    if (existing) {
      existing.style.display = 'block';
      existing.style.visibility = 'visible';
      existing.style.opacity = '1';
    }
  }

  // --- ViewSonic nav/menu link stripper -----------------------------------
  // DisplayDetails no longer sells ViewSonic. The nav-menu item still lives
  // in Shopify admin (Online Store → Navigation), which we can't edit via
  // the Theme Access token. Strip it client-side on every page.
  //
  // Matches BOTH mega-menu and mobile-drawer instances. Removes the wrapping
  // <li> if present; otherwise removes the <a> itself. Also collapses empty
  // parent groupings (e.g. if ViewSonic was the only child of a Specialty
  // column and its header becomes orphaned).
  function stripViewSonicLinks() {
    var killed = 0;
    var links = document.querySelectorAll(
      'a[href*="viewsonic-digital-signage"],' +
      'a[href*="viewsonic-commercial-displays"],' +
      'a[href*="/collections/viewsonic"]'
    );
    for (var i = 0; i < links.length; i++) {
      var a = links[i];
      // Walk up to the nearest list-item or menu-card wrapper and remove that
      var kill =
        a.closest('li') ||
        a.closest('[class*="mega-menu__link"]') ||
        a.closest('[class*="menu-drawer__item"]') ||
        a.closest('[class*="menu__item"]') ||
        a;
      if (kill && kill.parentNode) {
        kill.parentNode.removeChild(kill);
        killed++;
      }
    }
    // Also kill any lingering text nodes that read "ViewSonic Digital Signage"
    // inside mobile drawers that use div-based nav instead of <li>
    if (killed > 0 && window.console && console.debug) {
      console.debug('[dd-content-fixes] stripped ' + killed + ' ViewSonic nav link(s)');
    }
  }

  // --- PDP redesign (Meridian-style) --------------------------------------
  // Adds: breadcrumb, brand label above title, accordion (Warranty/Shipping/
  // Returns/Install), Safe & Secure payment badges, Share buttons.
  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function detectBrandFromPath(path) {
    if (/\/products\/samsung-/i.test(path)) return 'Samsung';
    if (/\/products\/lg-/i.test(path)) return 'LG';
    if (/\/products\/sony-/i.test(path)) return 'Sony';
    if (/\/products\/nec-/i.test(path)) return 'NEC';
    if (/\/products\/planar-/i.test(path)) return 'Planar';
    if (/\/products\/sharp-/i.test(path)) return 'Sharp';
    return '';
  }

  function detectCollectionFromPath(path) {
    // Heuristic: map known series prefixes to human-readable collection names
    var m = path.match(/^\/products\/samsung-(qm|qb|qh|oh|om|qe|be)([a-z]?\d+)?/i);
    if (m) return { label: 'Samsung ' + m[1].toUpperCase() + ' Series', href: '/collections/samsung-' + m[1].toLowerCase() + '-series' };
    m = path.match(/^\/products\/lg-\d+(um|uh|vh|vm|uh5j|um5j)/i);
    if (m) return { label: 'LG ' + m[1].toUpperCase() + ' Series', href: '/collections/lg-' + m[1].toLowerCase() + '-series' };
    if (/\/products\/samsung-/i.test(path)) return { label: 'Samsung Commercial Displays', href: '/collections/samsung-commercial-displays' };
    if (/\/products\/lg-/i.test(path)) return { label: 'LG Commercial Displays', href: '/collections/lg-commercial-displays' };
    return { label: 'Commercial Displays', href: '/collections/all' };
  }

  function buildBreadcrumbHtml(path, productTitle) {
    var col = detectCollectionFromPath(path);
    return '' +
      '<nav class="dd-breadcrumb" aria-label="Breadcrumb">' +
        '<a href="/">Home</a>' +
        '<span class="dd-crumb-sep">&rsaquo;</span>' +
        '<a href="' + col.href + '">' + escapeHtml(col.label) + '</a>' +
        '<span class="dd-crumb-sep">&rsaquo;</span>' +
        '<span class="dd-crumb-current">' + escapeHtml(productTitle) + '</span>' +
      '</nav>';
  }

  function buildAccItem(label, iconSvg, bodyHtml, openDefault) {
    var openCls = openDefault ? ' is-open' : '';
    return '' +
      '<div class="dd-accordion-item' + openCls + '">' +
        '<button type="button" class="dd-accordion-btn" aria-expanded="' + (openDefault ? 'true' : 'false') + '">' +
          '<span class="dd-acc-btn-label">' + iconSvg + '<span>' + escapeHtml(label) + '</span></span>' +
          '<svg class="dd-acc-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>' +
        '</button>' +
        '<div class="dd-accordion-body">' +
          '<div class="dd-accordion-body-inner">' + bodyHtml + '</div>' +
        '</div>' +
      '</div>';
  }

  function buildAccordionHtml() {
    var iconShield = '<svg class="dd-acc-icon-left" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>';
    var iconTruck = '<svg class="dd-acc-icon-left" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>';
    var iconReturn = '<svg class="dd-acc-icon-left" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>';
    var iconTool = '<svg class="dd-acc-icon-left" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>';

    return '' +
      '<div class="dd-pdp-accordion">' +
        buildAccItem('Authorized Samsung Reseller — Manufacturer Warranty',
          iconShield,
          '<p>As an <strong>Authorized Samsung Reseller</strong>, DisplayDetails sells genuine Samsung and LG commercial displays. Your unit ships with the standard <strong>manufacturer warranty</strong> &mdash; coverage terms vary by model. Contact us at <a href="mailto:hello@displaydetails.com">hello@displaydetails.com</a> for the warranty terms on this specific SKU.</p>',
          true) +
        buildAccItem('Professional Installation Available',
          iconTool,
          '<p>Professional installation is available and is scheduled at quote stage. Low-voltage technicians handle mount engineering, cable routing, and onboarding.</p>' +
          '<div class="dd-install-addons">' +
            '<div class="dd-install-addons-title">Optional add-on services</div>' +
            '<label class="dd-install-addon">' +
              '<input type="checkbox" class="dd-addon-toggle" data-dd-service="install" />' +
              '<span class="dd-install-addon-body">' +
                '<strong>Professional Installation</strong>' +
                '<em>Site survey, mount, cable, power &mdash; all 50 states. Quoted per location.</em>' +
              '</span>' +
            '</label>' +
            '<label class="dd-install-addon">' +
              '<input type="checkbox" class="dd-addon-toggle" data-dd-service="cms" />' +
              '<span class="dd-install-addon-body">' +
                '<strong>Cloud CMS</strong>' +
                '<em>Cloud content-management platform. $29.99/mo per screen, cancel anytime.</em>' +
              '</span>' +
            '</label>' +
            '<a href="/pages/contact-us" class="dd-install-quote-cta" data-dd-install-cta>Request a tailored quote &rsaquo;</a>' +
          '</div>',
          false) +
      '</div>';
  }

  function buildPaymentsHtml() {
    var iconLock = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>';
    var brands = ['Visa', 'Mastercard', 'Amex', 'Discover', 'Apple Pay', 'Google Pay', 'Shop Pay'];
    var badges = '';
    for (var i = 0; i < brands.length; i++) badges += '<span class="dd-pay-badge">' + brands[i] + '</span>';
    return '' +
      '<div class="dd-pdp-payments">' +
        '<div class="dd-pdp-payments-title">' + iconLock + '<span>Safe &amp; Secure Checkout</span></div>' +
        '<div class="dd-pdp-payments-list">' + badges + '</div>' +
        '<p class="dd-pdp-payments-note">256-bit SSL encrypted checkout. Your payment details never touch our servers.</p>' +
      '</div>';
  }

  function buildShareHtml(productTitle) {
    var url = encodeURIComponent(location.origin + location.pathname);
    var title = encodeURIComponent(productTitle || document.title);
    var xHref = 'https://twitter.com/intent/tweet?text=' + title + '&url=' + url;
    var fbHref = 'https://www.facebook.com/sharer/sharer.php?u=' + url;
    var liHref = 'https://www.linkedin.com/sharing/share-offsite/?url=' + url;
    var mailHref = 'mailto:?subject=' + title + '&body=' + url;

    var iX = '<svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M18.244 2H21.5l-7.54 8.62L22.5 22h-6.828l-5.344-6.99L4.2 22H1l8.06-9.2L1.5 2h7.01l4.83 6.4L18.244 2zm-1.196 18h1.885L7.03 4H5.02l12.028 16z"/></svg>';
    var iFb = '<svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.77-3.89 1.1 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.77l-.44 2.89h-2.33v6.99A10 10 0 0 0 22 12z"/></svg>';
    var iLi = '<svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M4.98 3.5C4.98 4.88 3.87 6 2.5 6S.02 4.88.02 3.5 1.13 1 2.5 1s2.48 1.12 2.48 2.5zM.22 22h4.56V8.24H.22V22zm7.55-13.76H12v1.9h.06c.56-1.05 1.94-2.16 3.99-2.16 4.27 0 5.06 2.81 5.06 6.46V22h-4.56v-6.44c0-1.53-.03-3.5-2.14-3.5-2.14 0-2.47 1.67-2.47 3.4V22H7.77V8.24z"/></svg>';
    var iMail = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>';
    var iCopy = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>';

    return '' +
      '<div class="dd-pdp-share">' +
        '<span class="dd-pdp-share-label">Share:</span>' +
        '<a class="dd-pdp-share-link" href="' + xHref + '" target="_blank" rel="noopener" aria-label="Share on X">' + iX + '</a>' +
        '<a class="dd-pdp-share-link" href="' + fbHref + '" target="_blank" rel="noopener" aria-label="Share on Facebook">' + iFb + '</a>' +
        '<a class="dd-pdp-share-link" href="' + liHref + '" target="_blank" rel="noopener" aria-label="Share on LinkedIn">' + iLi + '</a>' +
        '<a class="dd-pdp-share-link" href="' + mailHref + '" aria-label="Share via email">' + iMail + '</a>' +
        '<a class="dd-pdp-share-link dd-pdp-share-copy" href="#" aria-label="Copy link" data-dd-copy="' + escapeHtml(location.href) + '">' + iCopy + '</a>' +
      '</div>';
  }

  function wireAccordions(root) {
    var btns = root.querySelectorAll('.dd-accordion-btn');
    for (var i = 0; i < btns.length; i++) {
      (function (btn) {
        btn.addEventListener('click', function () {
          var item = btn.closest('.dd-accordion-item');
          if (!item) return;
          var open = item.classList.toggle('is-open');
          btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
      })(btns[i]);
    }

    // Wire optional-services checkboxes → update tailored-quote CTA href
    // with ?services=install,cms and include the current product handle so the
    // contact page can prefill the enquiry.
    var addonToggles = root.querySelectorAll('.dd-addon-toggle');
    var addonCta = root.querySelector('[data-dd-install-cta]');
    if (addonToggles.length && addonCta) {
      function rebuildCta() {
        var services = [];
        for (var a = 0; a < addonToggles.length; a++) {
          if (addonToggles[a].checked) {
            services.push(addonToggles[a].getAttribute('data-dd-service'));
          }
        }
        var params = [];
        if (services.length) params.push('services=' + encodeURIComponent(services.join(',')));
        // Include the product handle so the contact form has context
        var handleMatch = location.pathname.match(/\/products\/([^\/\?#]+)/);
        if (handleMatch && handleMatch[1]) {
          params.push('product=' + encodeURIComponent(handleMatch[1]));
        }
        addonCta.href = '/pages/contact-us' + (params.length ? '?' + params.join('&') : '');

        // Swap label based on selection so it reads naturally
        if (services.length === 0) {
          addonCta.textContent = 'Request a tailored quote \u203A';
        } else if (services.length === 1 && services[0] === 'install') {
          addonCta.textContent = 'Request installation quote \u203A';
        } else if (services.length === 1 && services[0] === 'cms') {
          addonCta.textContent = 'Request CMS quote \u203A';
        } else {
          addonCta.textContent = 'Request installation + CMS quote \u203A';
        }
      }
      for (var t = 0; t < addonToggles.length; t++) {
        addonToggles[t].addEventListener('change', rebuildCta);
      }
      // Initial build (sets product handle param)
      rebuildCta();
    }

    var copyLinks = root.querySelectorAll('.dd-pdp-share-copy');
    for (var j = 0; j < copyLinks.length; j++) {
      (function (el) {
        el.addEventListener('click', function (e) {
          e.preventDefault();
          try {
            navigator.clipboard.writeText(el.getAttribute('data-dd-copy') || location.href).then(function () {
              el.setAttribute('aria-label', 'Link copied!');
              el.style.background = '#0E9F6E';
              el.style.color = '#fff';
              setTimeout(function () { el.style.background = ''; el.style.color = ''; }, 1500);
            });
          } catch (err) {}
        });
      })(copyLinks[j]);
    }
  }

  // Clean product title text in place: strip leading brand word and fix
  // raw inch double-quote to proper prime (″). Preserves child-node structure.
  function cleanTitleText(titleEl, brand) {
    if (!titleEl) return;
    var brandRx = brand ? new RegExp('^\\s*' + brand + '\\s+', 'i') : null;
    // Walk text nodes under the title and transform
    var walker = document.createTreeWalker(titleEl, NodeFilter.SHOW_TEXT, null);
    var first = true, n;
    while ((n = walker.nextNode())) {
      var v = n.nodeValue;
      if (!v) continue;
      if (first && brandRx && brandRx.test(v)) {
        v = v.replace(brandRx, '');
        first = false;
      } else if (first && v.trim()) {
        first = false;
      }
      // Convert 98" → 98″ (inch mark) but not within other chars
      v = v.replace(/(\d+(?:\.\d+)?)\s*"/g, '$1\u2033');
      n.nodeValue = v;
    }
  }

  function enhancePDP(path) {
    if (path.indexOf('/products/') !== 0) return;

    var infoCol =
      document.querySelector('.product-information') ||
      document.querySelector('[class*="product-information"]') ||
      document.querySelector('[class*="_product_details"]');
    if (!infoCol) return;

    var titleEl =
      infoCol.querySelector('h1') ||
      infoCol.querySelector('[class*="product_title"]') ||
      infoCol.querySelector('.product__title');
    var brand = detectBrandFromPath(path);

    // 1. Breadcrumb — insert at very top
    if (!infoCol.querySelector('.dd-breadcrumb')) {
      var productTitle = titleEl ? (titleEl.textContent || '').trim() : document.title;
      // Strip leading "Samsung " / "LG " from breadcrumb current label too
      if (brand) productTitle = productTitle.replace(new RegExp('^\\s*' + brand + '\\s+', 'i'), '');
      productTitle = productTitle.replace(/(\d+(?:\.\d+)?)\s*"/g, '$1\u2033');
      var bc = document.createElement('div');
      bc.innerHTML = buildBreadcrumbHtml(path, productTitle);
      infoCol.insertBefore(bc.firstChild, infoCol.firstChild);
    }

    // 2. Build a clean custom hero block (brand label + styled title) that
    //    REPLACES the visual rendering of Horizon's native H1. The native H1
    //    stays in the DOM (visually hidden) so SEO and any theme JS that
    //    references it continue to work.
    if (titleEl && !infoCol.querySelector('.dd-pdp-hero')) {
      var rawTitle = (titleEl.textContent || '').trim();
      // Strip redundant leading brand
      var cleanTitle = rawTitle;
      if (brand) {
        cleanTitle = cleanTitle.replace(new RegExp('^\\s*' + brand + '\\s+', 'i'), '');
      }
      // Fix raw inch double-quote to proper prime (″)
      cleanTitle = cleanTitle.replace(/(\d+(?:\.\d+)?)\s*"/g, '$1\u2033');
      // Collapse whitespace
      cleanTitle = cleanTitle.replace(/\s+/g, ' ').trim();

      var hero = document.createElement('div');
      hero.className = 'dd-pdp-hero';
      var brandHtml = brand ? '<div class="dd-pdp-brand">' + escapeHtml(brand) + '</div>' : '';
      hero.innerHTML =
        brandHtml +
        '<h1 class="dd-pdp-title">' + escapeHtml(cleanTitle) + '</h1>';

      // Insert hero directly before the native H1 and mark native H1 hidden
      titleEl.parentNode.insertBefore(hero, titleEl);
      titleEl.classList.add('dd-native-h1-hidden');
      titleEl.dataset.ddTitleCleaned = '1';
    }

    // 2c. Remove stray horizontal rules inside the info column — they create
    // the "white gap" Ben flagged above the stock / quantity row.
    var hrs = infoCol.querySelectorAll('hr, [role="separator"]');
    for (var h = 0; h < hrs.length; h++) {
      try { hrs[h].style.display = 'none'; } catch (e) {}
    }
    // Also hide any empty spacer blocks that Horizon sometimes injects
    var spacers = infoCol.querySelectorAll('[class*="spacer"], [class*="_spacer"]');
    for (var s = 0; s < spacers.length; s++) {
      var sp = spacers[s];
      if (!sp.textContent || !sp.textContent.trim()) {
        try { sp.style.display = 'none'; } catch (e) {}
      }
    }
    // 2d. Kill any late-injected yellow "Free shipping" urgency banner
    var urgencies = document.querySelectorAll('.dd-urgency');
    for (var u = 0; u < urgencies.length; u++) {
      try { urgencies[u].remove(); } catch (e) {}
    }

    // 2e. Collapse empty sibling blocks inside product info. Horizon renders
    // several wrapper blocks that can end up empty on products with only the
    // default variant (e.g. the variant picker), and those empty wrappers
    // still hold margins/padding that create a huge white gap between the
    // price and the stock badge. Walk the info column and hide any element
    // that has no visible text, no images, no SVGs, and no form controls.
    function blockIsVisuallyEmpty(el) {
      if (!el || el.nodeType !== 1) return false;
      if (el.dataset.ddHeroKeep === '1') return false;
      // Keep things we rely on
      if (el.classList.contains('dd-pdp-hero')) return false;
      if (el.classList.contains('dd-stock-badge')) return false;
      if (el.classList.contains('dd-multiunit-btn')) return false;
      if (el.classList.contains('dd-why-buy')) return false;
      if (el.classList.contains('dd-pdp-bottom')) return false;
      if (el.classList.contains('dd-breadcrumb')) return false;
      if (el.tagName === 'PRODUCT-PRICE') return false;
      if (el.tagName === 'PRODUCT-FORM') return false;
      if (el.tagName === 'FORM') return false;
      if (el.tagName === 'SCRIPT' || el.tagName === 'STYLE' || el.tagName === 'NOSCRIPT') return false;
      if (el.querySelector('img, svg, input, select, textarea, button, product-price, product-form, form, variant-picker, quantity-input')) return false;
      var t = (el.textContent || '').replace(/\s+/g, '').trim();
      return t.length === 0;
    }
    var infoChildren = infoCol.querySelectorAll(':scope > *, :scope > * > *');
    for (var ec = 0; ec < infoChildren.length; ec++) {
      var child = infoChildren[ec];
      if (blockIsVisuallyEmpty(child)) {
        try { child.style.display = 'none'; } catch (e) {}
      }
    }

    // 3. Bottom block: accordion + payments + share, after buy-buttons
    if (!infoCol.querySelector('.dd-pdp-bottom')) {
      var bottom = document.createElement('div');
      bottom.className = 'dd-pdp-bottom';
      bottom.innerHTML = buildAccordionHtml() + buildPaymentsHtml() + buildShareHtml(document.title);

      var anchor =
        infoCol.querySelector('[class*="buy-buttons"]') ||
        infoCol.querySelector('[class*="_buy_buttons"]') ||
        infoCol.querySelector('product-form') ||
        infoCol.querySelector('form[action*="/cart/add"]');
      if (anchor && anchor.parentNode) {
        var wrap = anchor.closest('[class*="buy-buttons"]') || anchor.closest('product-form') || anchor;
        if (wrap.nextSibling) wrap.parentNode.insertBefore(bottom, wrap.nextSibling);
        else wrap.parentNode.appendChild(bottom);
      } else {
        infoCol.appendChild(bottom);
      }
      wireAccordions(bottom);
    }
  }

  // --- Collection card enhancer (Nikon-style) -----------------------------
  // Adds: brand label above card title, stock pill with colored dot.
  function enhanceCollectionCards() {
    var onCollection = /^\/collections\//.test(location.pathname);
    var onSearch = /^\/search/.test(location.pathname);
    if (!onCollection && !onSearch) return;

    // Horizon product cards — try a few selectors
    var cards = document.querySelectorAll(
      'product-card, .product-card, [class*="product-card"], .card-product, [class*="_product_card"]'
    );
    if (!cards.length) return;

    cards.forEach(function (card) {
      if (card.dataset.ddEnhanced === '1') return;
      card.dataset.ddEnhanced = '1';

      // Find title link → derive handle → derive brand
      var titleLink =
        card.querySelector('a[href*="/products/"]') ||
        card.querySelector('.product-card__title a') ||
        card.querySelector('[class*="product_title"] a');
      if (!titleLink) return;
      var href = (titleLink.getAttribute('href') || '').toLowerCase();
      var brand = detectBrandFromPath(href);

      // Inject brand label above the title text
      var titleContainer =
        card.querySelector('.product-card__title') ||
        card.querySelector('[class*="product_title"]') ||
        titleLink.parentNode;
      if (brand && titleContainer && !titleContainer.querySelector('.dd-card-brand')) {
        var lbl = document.createElement('span');
        lbl.className = 'dd-card-brand';
        lbl.textContent = brand;
        titleContainer.insertBefore(lbl, titleContainer.firstChild);
      }

      // Clean the card title text: strip redundant brand + fix inch mark
      if (titleLink && !titleLink.dataset.ddTitleCleaned) {
        cleanTitleText(titleLink, brand);
        titleLink.dataset.ddTitleCleaned = '1';
      }

      // Mark price block with a dd class for targeted styling
      var cardPrice =
        card.querySelector('product-price') ||
        card.querySelector('[class*="product_price"]') ||
        card.querySelector('.price');
      if (cardPrice) cardPrice.classList.add('dd-card-price');

      // Stock pill — default In Stock; will be marked out/low if card already
      // shows those cues. Shopify Horizon exposes "Sold out" / "Out of stock"
      // as a badge element when applicable.
      if (!card.querySelector('.dd-card-stock')) {
        var cardText = (card.textContent || '').toLowerCase();
        var soldOut = /sold out|out of stock/i.test(cardText);
        var lowStock = /only \d+ left|low stock|hurry|limited/i.test(cardText);
        var pill = document.createElement('span');
        pill.className = 'dd-card-stock';
        if (soldOut) { pill.className += ' is-out'; pill.textContent = 'Out of stock'; }
        else if (lowStock) { pill.className += ' is-low'; pill.textContent = 'Low stock'; }
        else {
          var ddLink = card.querySelector('a[href*="/products/"]');
          var ddHandle = ddLink ? ((ddLink.getAttribute('href') || '').split('/products/')[1] || '').split(/[?#\/]/)[0] : '';
          var ddStocked = (window.DD_IN_STOCK_HANDLES || []).indexOf(ddHandle) > -1;
          pill.textContent = ddStocked ? 'In stock \u00B7 ships in 5\u20137 business days' : 'Ships in 5\u20137 business days';
        }

        // Insert after price if present, else at end of card body
        var priceEl =
          card.querySelector('product-price') ||
          card.querySelector('[class*="product_price"]') ||
          card.querySelector('.price');
        if (priceEl && priceEl.parentNode) {
          if (priceEl.nextSibling) priceEl.parentNode.insertBefore(pill, priceEl.nextSibling);
          else priceEl.parentNode.appendChild(pill);
        } else {
          card.appendChild(pill);
        }
      }
    });
  }

  // --- Main ----------------------------------------------------------------
  function run() {
    try {
      var path = (location.pathname || '/').toLowerCase();
      var root = document.querySelector('main') || document.body;
      if (!root) return;

      // 1. Global text & attribute replacements
      walkTextNodes(root, GLOBAL);
      walkAttributes(root, GLOBAL);

      // 1a. Strip ViewSonic nav-menu links (admin-side entry we can't edit)
      stripViewSonicLinks();

      // 2. Page-specific text replacements
      var rules = null;
      for (var k in PAGE_RULES) {
        if (Object.prototype.hasOwnProperty.call(PAGE_RULES, k)) {
          if (path === k || path.indexOf(k) === 0) {
            rules = PAGE_RULES[k];
            break;
          }
        }
      }
      if (rules && rules.length) walkTextNodes(root, rules);

      // 3. Page-level content injections (pages/*)
      var injector = null;
      for (var p in PAGE_INJECTIONS) {
        if (Object.prototype.hasOwnProperty.call(PAGE_INJECTIONS, p)) {
          if (path === p || path === p + '/') {
            injector = PAGE_INJECTIONS[p];
            break;
          }
        }
      }
      if (injector) {
        var html = injector();
        if (html) {
          var target =
            document.querySelector('.rte') ||
            document.querySelector('.page-content') ||
            document.querySelector('main .container') ||
            document.querySelector('main') ||
            document.body;
          if (target) {
            var wrap = document.createElement('div');
            wrap.className = 'dd-canonical-override';
            wrap.innerHTML = html;
            // Insert at very top of the content area
            target.insertBefore(wrap, target.firstChild);
          }
        }
      }

      // 4. Product page: series summary + trust block on every product
      if (path.indexOf('/products/') === 0) {
        injectProductPageContent(path);
        ensureProductPrice();
        enhancePDP(path);
      }

      // 5. Blog cleanup
      applyBlogCleanup(path);

      // 6. Cart enhancements
      enhanceCartPage();

      // 7. Collection card redesign (Nikon-style)
      enhanceCollectionCards();
    } catch (e) {
      if (window.console && console.warn) console.warn('[dd-content-fixes] error:', e);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // Belt-and-suspenders: re-strip ViewSonic nav links a few times after
  // initial run, in case a mega-menu or mobile drawer hydrates late.
  [500, 1500, 3000].forEach(function (ms) {
    setTimeout(function () {
      try { stripViewSonicLinks(); } catch (e) {}
    }, ms);
  });

  // And if the DOM mutates (drawer opens, menu expands), strip again.
  if (typeof MutationObserver !== 'undefined') {
    try {
      var mo = new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          if (muts[i].addedNodes && muts[i].addedNodes.length) {
            stripViewSonicLinks();
            break;
          }
        }
      });
      var start = function () {
        var target = document.querySelector('header') || document.body;
        if (target) mo.observe(target, { childList: true, subtree: true });
      };
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start);
      } else {
        start();
      }
    } catch (e) {}
  }
})();
